import React, { useEffect, useState } from "react";
import classes from "./GTCSettings.css";
import axios from "axios";
import ConsentList from "../../components/ConsentList/ConsentList";
import Button from "../../components/UI/Button/Button";
import { Trans, useTranslation } from "react-i18next";
import Spinner from "../../components/UI/NvidiaSpinner/NvidiaSpinner";

import { getUrlParamByName, isUrlParamByName } from "../../utils/helpers";
import { VALIDATIONS } from "../PrivacySettings/config";

const GTCSettings = (props) => {
  const [loading, setLoading] = useState(true);
  const [state] = useState(getUrlParamByName(props.location.search, "state"));
  const [lang] = props.locale.split("_");
  const [consents, setConsents] = useState({
    trackBehavioralData: {
      value: false,
      required: isUrlParamByName(props.location.search, "trackBehavioralData"),
    },
    trackTechnicalData: {
      value: false,
      required: isUrlParamByName(props.location.search, "trackTechnicalData"),
    },
    opt_in: {
      value: false,
      required: isUrlParamByName(props.location.search, "opt_in"),
    },
  });

  const { t } = useTranslation();

  // loading the analytics script
  useEffect(() => {
    const scriptElement = document.createElement("script");
    scriptElement.src = ANALYTICS_URL;
    document.head.appendChild(scriptElement);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);

      let data = await getConsent(
        props.match.params.serviceType,
        props.match.params.version
      );

      setConsents({
        ...consents,
        trackBehavioralData: {
          ...consents.trackBehavioralData,
          ...data.trackBehavioralData,
        },
        trackTechnicalData: {
          ...consents.trackTechnicalData,
          ...data.trackTechnicalData,
        },
        opt_in: {
          ...consents.opt_in,
          ...data.opt_in,
        },
      });

      setLoading(false);
    })();
  }, []);

  const getConsent = async (serviceType, version) => {
    const response = await axios.get(
      `${AEM_CONSENT_CONTENT}${props.locale}.${serviceType}.${version}.json`,
      { params: { v: 2 } }
    );
    return response.data.consent[serviceType][version][lang];
  };

  const getConsentArray = () => {
    let consentsArray = [];
    for (let consentName in consents) {
      if (consents[consentName].required) {
        consentsArray.push({
          ...consents[consentName],
          name: consentName,
        });
      }
    }
    return consentsArray;
  };

  const handleConsentChange = (event) => {
    setConsents({
      ...consents,
      [event.target.name]: {
        ...consents[event.target.name],
        value: event.target.checked,
      },
    });
  };

  const unvalidatedRedirectUri = decodeURIComponent(
    getUrlParamByName(props.location.search, "redirect_uri")
  );
  const isRedirectUriValid = unvalidatedRedirectUri.match(VALIDATIONS.urlRegex);
  const redirectUri = isRedirectUriValid ? unvalidatedRedirectUri : "/";

  return (
    <div className={classes.GTCWrapper}>
      <div className={classes.GTCContainer}>
        <div className={classes.GTCContainerHeader}>
          <div className={classes.GTCContainerHeaderTitle}>
            {t("Complete your profile")}
          </div>
          <div className={classes.GTCContainerHeaderNote}>
            {t(
              "Confirm the information below to complete your NVIDIA account profile."
            )}
          </div>
        </div>
        <div className={classes.GTCMain}>
          {loading ? (
            <Spinner inline="true" />
          ) : (
            <ConsentList
              handleConsentChange={handleConsentChange}
              consents={getConsentArray()}
              GTC={true}
            />
          )}

          <div className={classes.spacer} />
          <Trans>
            We promise to protect your privacy. We never sell your data. You can
            change your settings any time at{" "}
            <a className={classes.Anchor} href="https://privacy.nvidia.com/">
              privacy.nvidia.com
            </a>
            .
          </Trans>
        </div>
        <div className={classes.GTCAction}>
          <form
            method="post"
            action={redirectUri}
            onSubmit={(e) => {
              if (!isRedirectUriValid) {
                e.preventDefault();
                alert("Invalid redirect uri!");
              } else {
                setLoading(true);
              }
            }}
          >
            {Object.keys(consents).map((consent, index) => (
              <input
                key={index}
                name={consent}
                value={consents[consent].value}
                type="hidden"
              />
            ))}
            {state ? <input name="state" value={state} type="hidden" /> : null}
            <Button disabled={loading} label={t("Continue")} type="submit" />
          </form>
        </div>

        <div className={classes.GTCConsentNote}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
          >
            <g
              id="Group_11044"
              data-name="Group 11044"
              transform="translate(-24643 -2209)"
            >
              <rect
                id="Rectangle_4424"
                data-name="Rectangle 4424"
                width="16"
                height="16"
                transform="translate(24643 2209)"
                fill="none"
              />
              <path
                id="info_FILL0_wght600_GRAD0_opsz48"
                d="M9.65,13.6h1.2V9.5H9.65Zm.55-5.2a.666.666,0,0,0,.492-.192.645.645,0,0,0,.192-.475.687.687,0,0,0-.192-.508.726.726,0,0,0-.983,0,.687.687,0,0,0-.192.508.645.645,0,0,0,.192.475A.666.666,0,0,0,10.2,8.4Zm0,8.667a6.675,6.675,0,0,1-2.683-.542A6.992,6.992,0,0,1,3.842,12.85,6.675,6.675,0,0,1,3.3,10.167a6.717,6.717,0,0,1,.542-2.7A6.98,6.98,0,0,1,5.317,5.283,7.071,7.071,0,0,1,7.508,3.8a6.9,6.9,0,0,1,5.4,0,6.931,6.931,0,0,1,3.658,3.658,6.9,6.9,0,0,1,0,5.4,7.071,7.071,0,0,1-1.483,2.192A6.98,6.98,0,0,1,12.9,16.525,6.717,6.717,0,0,1,10.2,17.067Zm.017-1.317a5.354,5.354,0,0,0,3.942-1.633,5.414,5.414,0,0,0,1.625-3.967,5.367,5.367,0,0,0-1.625-3.942A5.387,5.387,0,0,0,10.2,4.583,5.4,5.4,0,0,0,6.25,6.208a5.373,5.373,0,0,0-1.633,3.958,5.381,5.381,0,0,0,1.633,3.95A5.4,5.4,0,0,0,10.217,15.75ZM10.2,10.167Z"
                transform="translate(24641.033 2206.75)"
              />
            </g>
          </svg>
          {t(
            "As a reminder, each person participating in GTC must register individually."
          )}
        </div>
      </div>
    </div>
  );
};

export default GTCSettings;
